import { useEffect } from 'react';
import { Faders } from '@phosphor-icons/react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SearchBar from '@Common/SearchBar';
import PromptSelect from '@Components/analyzer/controls/PromptSelect';
import FilteredItem from '@Components/analyzer/views/item/FilteredItem';
import AddPromptModal from '@Components/analyzer/views/modals/AddPromptModal';
import ItemModal from '@Components/analyzer/views/modals/ItemModal';
import JiraItemModal from '@Components/analyzer/views/modals/JiraItemModal';
import ViewPromptModal from '@Components/analyzer/views/modals/ViewPromptModal';
import TokenCount from '@Components/analyzer/views/token/TokenCount';
import { promptAnalyzer } from '@Models/types';
import { BounceLoader, MoonLoader } from 'react-spinners';
import { CaretDown } from '@phosphor-icons/react';
import colors from '@Common/assets/colors';
import { useAuth } from '@Context/CognitoAuthProvider';
import { analyzerAPI } from '@Lib/axios/analyzer';
import { useAnalyzerContext } from '@Context/AnalyzerContext';
import AhaPods from './components/AhaPods';
import JiraProjects from './components/JiraProjects';

const FilterOptions = [
  {
    id: 'confidenceDesc',
    label: 'Confidence Descending',
  },
  {
    id: 'confidenceAsc',
    label: 'Confidence Ascending',
  },
  { id: 'pod', label: 'Pod' },
  { id: 'timeframeNewest', label: 'Newest' },
  { id: 'timeframeOldest', label: 'Oldest' },
];

function Analyzer() {
  const {
    // State
    search,
    setSearch,
    showAddPrompt,
    setShowAddPrompt,
    showItemModal,
    setShowItemModal,
    showViewPrompt,
    setShowViewPrompt,
    sortDropdownOpen,
    setSortDropdownOpen,
    filteredRecords,
    filteredIssues,
    taggedRecords,
    taggedIssues,
    loadingRecords,
    loadingIssues,
    selectedRecord,
    setSelectedRecord,
    selectedIssue,
    setSelectedIssue,
    showCancel,
    setShowCancel,
    showSuggestions,
    setShowSuggestions,
    selectedPrompt,
    setSelectedPrompt,
    sortOption,
    setSortOption,
    isHighOpen,
    setIsHighOpen,
    isMidOpen,
    setIsMidOpen,
    isLowOpen,
    setIsLowOpen,
    globalLoading,
    tagLoading,
    tokenData,
    promptOptions,
    setPromptOptions,
    // Actions
    isJiraMode,
    setJiraMode,
    handleIdentify,
    cancelProcess,
    handleTagTicket,
    handleSaveTagged,
    handleClearTagged,
    handleItemClick,
    onDragEnd,
    // Computed values
    suggestions,
    highTickets,
    midTickets,
    lowTickets,
    highIssues,
    midIssues,
    lowIssues,
  } = useAnalyzerContext();

  const { logout } = useAuth();
  const analyzer = analyzerAPI(logout);

  // Fetch prompts on mount
  useEffect(() => {
    analyzer
      .getAnalyzerPrompts()
      .then((data) => setPromptOptions(data))
      .catch((err) => console.error('Error fetching prompts:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh prompts when modal closes
  useEffect(() => {
    if (!showAddPrompt) {
      analyzer
        .getAnalyzerPrompts()
        .then(setPromptOptions)
        .catch((err) => console.error('Error refreshing prompts:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddPrompt]);

  // Show suggestions effect
  useEffect(() => {
    if (search.trim() !== '') {
      setShowSuggestions(true);
      const timer = setTimeout(() => setShowSuggestions(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const activeTaggedItems = isJiraMode ? taggedIssues : taggedRecords;

  const identifyButtonContent = () => {
    if (globalLoading) {
      return showCancel ? (
        'Cancel'
      ) : (
        <BounceLoader size={20} color={colors.brandWarning[500]} />
      );
    }
    return 'Identify';
  };

  return (
    <>
      {isJiraMode ? (
        <JiraItemModal
          isOpen={showItemModal}
          onCancel={() => {
            setShowItemModal(false);
            setSelectedIssue(null);
          }}
          record={selectedIssue}
          onTag={handleTagTicket}
        />
      ) : (
        <ItemModal
          isOpen={showItemModal}
          onCancel={() => {
            setShowItemModal(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord}
          onTag={handleTagTicket}
        />
      )}
      <ViewPromptModal
        isOpen={showViewPrompt}
        onCancel={() => setShowViewPrompt(false)}
        prompt={selectedPrompt}
        onPromptEdited={setPromptOptions}
      />
      <AddPromptModal
        isOpen={showAddPrompt}
        onCancel={() => setShowAddPrompt(false)}
        onPromptAdded={setPromptOptions}
      />
      <div className="h-[100vh] w-full bg-white md:p-8">
        <div className="h-full w-full border-gray-300 bg-white p-4 md:rounded-xl md:border-2 md:p-8">
          <div className="flex h-full flex-col bg-white">
            {/* header */}
            <div className="flex flex-col justify-between gap-5 bg-white md:flex-row md:gap-0">
              {/* select pods and filter */}
              <div className="relative flex w-full flex-row gap-2 bg-white md:w-[49%]">
                <div className="bg-white-600 flex items-end gap-2 md:items-center">
                  {isJiraMode ? (
                    <>
                      <JiraProjects
                        onModeSwitch={() => setJiraMode(false)}
                        disabled={globalLoading}
                      />
                    </>
                  ) : (
                    <>
                      <AhaPods
                        onModeSwitch={() => setJiraMode(true)}
                        disabled={globalLoading}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Identify + PromptSelect side */}
              <div className="flex justify-between">
                <div className="flex w-full flex-col justify-between">
                  <div className="flex w-full flex-col gap-2 md:w-[30vw]">
                    <div className="flex flex-col gap-2 md:flex-row">
                      <PromptSelect<promptAnalyzer>
                        value={
                          selectedPrompt
                            ? {
                                label: selectedPrompt.name,
                                value: selectedPrompt,
                              }
                            : null
                        }
                        onShowView={() => setShowViewPrompt(true)}
                        onShowPrompt={() => setShowAddPrompt(true)}
                        type="prompt"
                        options={promptOptions.map((prompt) => ({
                          label: prompt.name,
                          value: prompt,
                        }))}
                        onChange={(option) =>
                          setSelectedPrompt(option ? option.value : null)
                        }
                        placeholder={'Set Prompt'}
                        onPromptsUpdated={setPromptOptions}
                      />
                      <button
                        onMouseEnter={() => {
                          if (globalLoading) setShowCancel(true);
                        }}
                        onMouseLeave={() => {
                          setShowCancel(false);
                        }}
                        onClick={() => {
                          if (globalLoading && showCancel) {
                            cancelProcess();
                          } else if (!globalLoading) {
                            handleIdentify();
                          }
                        }}
                        className="flex h-10 items-center justify-center rounded-md bg-primary-100 px-10 text-center font-semibold text-primary-700 transition-colors hover:bg-primary-700 hover:text-white md:w-16"
                      >
                        {identifyButtonContent()}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <div className="mt-4 flex h-full flex-col gap-4 lg:flex-row">
                <div className="flex h-full flex-1 flex-col rounded-xl border-2">
                  <div className="flex flex-col justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-2 md:flex-row md:items-center">
                    <p className="flex-[1.3] font-semibold text-white">
                      Filtered Tickets
                    </p>
                    <div className="relative flex items-center gap-2">
                      <SearchBar
                        setSearch={setSearch}
                        search={search}
                        type={''}
                      />
                      {suggestions.length > 0 && showSuggestions && (
                        <ul className="absolute left-0 top-10 z-10 w-full rounded border bg-white shadow">
                          {suggestions.map((sug, index) => (
                            <li
                              key={index}
                              className="cursor-pointer px-2 py-1 hover:bg-gray-100"
                              onClick={() => {
                                setSearch(sug.name);
                                setShowSuggestions(false);
                              }}
                            >
                              {sug.name}
                            </li>
                          ))}{' '}
                        </ul>
                      )}
                      <button
                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                        className="text-primary-white relative flex items-center px-2 py-1"
                      >
                        <Faders
                          className="cursor-pointer text-white hover:text-primary-200"
                          size={23}
                          weight="bold"
                        />
                      </button>

                      {sortDropdownOpen && (
                        <div
                          onMouseLeave={() => setSortDropdownOpen(false)}
                          className="absolute right-0 top-0 z-10 mt-12 w-48 rounded border border-gray-300 bg-white p-2 shadow-lg"
                        >
                          <ul className="custom-scroll max-h-60 overflow-auto px-2 py-1 font-inter text-sm">
                            {FilterOptions.map((option) => (
                              <li
                                key={option.id}
                                onClick={() => {
                                  setSortOption(option.id);
                                  setSortDropdownOpen(false);
                                }}
                                className={`cursor-pointer rounded-md px-4 py-2 ${
                                  sortOption === option.id
                                    ? 'bg-primary-700 text-white'
                                    : 'hover:bg-primary-100'
                                }`}
                              >
                                {option.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="custom-scroll h-0 w-full flex-grow overflow-y-scroll p-2">
                    {(isJiraMode ? loadingIssues : loadingRecords) &&
                    (isJiraMode ? filteredIssues : filteredRecords).length ===
                      0 ? (
                      <div className="flex flex-col items-center pt-[5vh] md:pt-[15vh]">
                        <MoonLoader
                          size={100}
                          color={colors.brandWarning[500]}
                        />
                        <p className="mt-2">Loading records...</p>
                      </div>
                    ) : (
                      <>
                        {/* High Confidence Group */}
                        <div className="mb-4 rounded border">
                          <div
                            className="flex cursor-pointer items-center justify-between bg-gray-200 px-4 py-2 font-semibold"
                            onClick={() => setIsHighOpen(!isHighOpen)}
                          >
                            <span>High Confidence (70-100)</span>
                            <CaretDown
                              size={20}
                              className={`ml-2 transition-transform ${
                                isHighOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                          {isHighOpen && (
                            <Droppable droppableId="high">
                              {(providedHigh) => (
                                <div
                                  ref={providedHigh.innerRef}
                                  {...providedHigh.droppableProps}
                                  className="min-h-[50px] p-2"
                                >
                                  {(isJiraMode ? highIssues : highTickets)
                                    .length > 0 ? (
                                    (isJiraMode ? highIssues : highTickets).map(
                                      (record, index) => (
                                        <Draggable
                                          key={record.id}
                                          draggableId={record.id.toString()}
                                          index={index}
                                        >
                                          {(providedDraggable) => (
                                            <div
                                              ref={providedDraggable.innerRef}
                                              {...providedDraggable.draggableProps}
                                              {...providedDraggable.dragHandleProps}
                                            >
                                              <FilteredItem
                                                isOpen={true}
                                                onClick={() =>
                                                  handleItemClick(record)
                                                }
                                                title={`${record.reference_num} - ${record.name}`}
                                                content={
                                                  (record.description ||
                                                    'No description available') +
                                                  (record.gptClassification
                                                    ?.error
                                                    ? ' (Classification failed)'
                                                    : ' (Classification successful)')
                                                }
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    )
                                  ) : (
                                    <p className="p-2 text-gray-500">
                                      No high confidence tickets
                                    </p>
                                  )}
                                  {providedHigh.placeholder}
                                </div>
                              )}
                            </Droppable>
                          )}
                        </div>

                        {/* Mid Confidence Group */}
                        <div className="mb-4 rounded border">
                          <div
                            className="flex cursor-pointer items-center justify-between bg-gray-200 px-4 py-2 font-semibold"
                            onClick={() => setIsMidOpen(!isMidOpen)}
                          >
                            <span>Mid Confidence (30-69)</span>
                            <CaretDown
                              size={20}
                              className={`ml-2 transition-transform ${
                                isMidOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                          {isMidOpen && (
                            <Droppable droppableId="mid">
                              {(providedMid) => (
                                <div
                                  ref={providedMid.innerRef}
                                  {...providedMid.droppableProps}
                                  className="min-h-[50px] p-2"
                                >
                                  {(isJiraMode ? midIssues : midTickets)
                                    .length > 0 ? (
                                    (isJiraMode ? midIssues : midTickets).map(
                                      (record, index) => (
                                        <Draggable
                                          key={record.id}
                                          draggableId={record.id.toString()}
                                          index={index}
                                        >
                                          {(providedDraggable) => (
                                            <div
                                              ref={providedDraggable.innerRef}
                                              {...providedDraggable.draggableProps}
                                              {...providedDraggable.dragHandleProps}
                                            >
                                              <FilteredItem
                                                isOpen={true}
                                                onClick={() =>
                                                  handleItemClick(record)
                                                }
                                                title={`${record.reference_num} - ${record.name}`}
                                                content={
                                                  (record.description ||
                                                    'No description available') +
                                                  (record.gptClassification
                                                    ?.error
                                                    ? ' (Classification failed)'
                                                    : ' (Classification successful)')
                                                }
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    )
                                  ) : (
                                    <p className="p-2 text-gray-500">
                                      No mid confidence tickets
                                    </p>
                                  )}
                                  {providedMid.placeholder}
                                </div>
                              )}
                            </Droppable>
                          )}
                        </div>

                        {/* Low Confidence Group */}
                        <div className="mb-4 rounded border">
                          <div
                            className="flex cursor-pointer items-center justify-between bg-gray-200 px-4 py-2 font-semibold"
                            onClick={() => setIsLowOpen(!isLowOpen)}
                          >
                            <span>Low Confidence (&lt;30)</span>
                            <CaretDown
                              size={20}
                              className={`ml-2 transition-transform ${
                                isLowOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                          {isLowOpen && (
                            <Droppable droppableId="low">
                              {(providedLow) => (
                                <div
                                  ref={providedLow.innerRef}
                                  {...providedLow.droppableProps}
                                  className="min-h-[50px] p-2"
                                >
                                  {(isJiraMode ? lowIssues : lowTickets)
                                    .length > 0 ? (
                                    (isJiraMode ? lowIssues : lowTickets).map(
                                      (record, index) => (
                                        <Draggable
                                          key={record.id}
                                          draggableId={record.id.toString()}
                                          index={index}
                                        >
                                          {(providedDraggable) => (
                                            <div
                                              ref={providedDraggable.innerRef}
                                              {...providedDraggable.draggableProps}
                                              {...providedDraggable.dragHandleProps}
                                            >
                                              <FilteredItem
                                                isOpen={true}
                                                onClick={() =>
                                                  handleItemClick(record)
                                                }
                                                title={`${record.reference_num} - ${record.name}`}
                                                content={
                                                  (record.description ||
                                                    'No description available') +
                                                  (record.gptClassification
                                                    ?.error
                                                    ? ' (Classification failed)'
                                                    : ' (Classification successful)')
                                                }
                                              />
                                            </div>
                                          )}
                                        </Draggable>
                                      )
                                    )
                                  ) : (
                                    <p className="p-2 text-gray-500">
                                      No low confidence tickets
                                    </p>
                                  )}
                                  {providedLow.placeholder}
                                </div>
                              )}
                            </Droppable>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Tagged Column */}
                <Droppable droppableId="tagged" isDropDisabled={tagLoading}>
                  {(providedTagged) => (
                    <div
                      className="flex h-full flex-1 flex-col rounded-xl border-2"
                      ref={providedTagged.innerRef}
                      {...providedTagged.droppableProps}
                    >
                      <div className="flex h-12 items-center justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-3">
                        <p className="flex-[1.3] font-semibold text-white">
                          Tagged
                        </p>
                        <div className="flex-1">
                          <div className="flex flex-1 justify-end gap-2">
                            <button
                              onClick={handleSaveTagged}
                              disabled={tagLoading || globalLoading}
                              className={`flex w-10 items-center justify-center rounded-md px-10 text-center font-semibold transition-all duration-200 focus:outline-none active:scale-95 ${
                                tagLoading
                                  ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                                  : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                              } `}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleClearTagged}
                              disabled={tagLoading}
                              className={`flex w-10 items-center justify-center rounded-md px-10 text-center font-semibold transition-all duration-200 focus:outline-none active:scale-95 ${
                                tagLoading
                                  ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                                  : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                              }`}
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`custom-scroll h-0 w-full flex-grow gap-2 overflow-y-scroll p-2 ${
                          tagLoading ? 'pointer-events-none opacity-80' : ''
                        }`}
                      >
                        {tagLoading ? (
                          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
                            <MoonLoader color="#071D49" />
                            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
                              Please wait while we save your tagged tickets...
                            </span>
                          </span>
                        ) : activeTaggedItems.length === 0 ? (
                          <p className="p-2 text-gray-500">
                            {' '}
                            No tagged tickets
                          </p>
                        ) : (
                          activeTaggedItems.map((record, index) => (
                            <Draggable
                              key={record.id}
                              draggableId={record.id.toString()}
                              index={index}
                              isDragDisabled={tagLoading}
                            >
                              {(providedDraggable) => (
                                <div
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  {...providedDraggable.dragHandleProps}
                                >
                                  <FilteredItem
                                    isOpen={true}
                                    onClick={() => handleItemClick(record)}
                                    title={`${record.reference_num} - ${record.name}`}
                                    content={
                                      (record.description ||
                                        'No description available') +
                                      (record.gptClassification?.error
                                        ? ' (Classification failed)'
                                        : ' (Classification successful)')
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))
                        )}
                        {providedTagged.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </div>
          <TokenCount
            tokenCount={{
              promptTokens: tokenData?.promptTokens || 0,
            }}
          />
        </div>
      </div>
    </>
  );
}

export default Analyzer;
